import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '@/lib/firebase';
import { collection, query, getDocs, orderBy, where, limit, startAfter, deleteDoc, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { Navbar } from '@/components/dashboard/Navbar';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { ServiceCard } from '@/components/services/ServiceCard';
import { ServiceFilters } from '@/components/services/ServiceFilters';
import { toast } from 'sonner';

const CATEGORIES = [
  'Todos',
  'Elétrica',
  'Hidráulica',
  'Pintura',
  'Alvenaria',
  'Marcenaria',
  'Jardinagem',
  'Limpeza',
  'Outros'
];

const PAGE_SIZE = 10;

interface ServiceRequest {
  id: string;
  title: string;
  description: string;
  category: string;
  location: string;
  createdAt: any;
  status: 'open' | 'in_progress' | 'completed';
  urgency: 'low' | 'medium' | 'high';
  authorId: string;
  budget: number;
}

export default function ServicesPage() {
  const [services, setServices] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadServices();
  }, [selectedCategory]);

  const loadServices = async (isLoadMore = false) => {
    try {
      setLoading(true);
      let q = query(
        collection(db, 'services'),
        orderBy('createdAt', 'desc'),
        limit(PAGE_SIZE)
      );

      if (selectedCategory !== 'Todos') {
        q = query(q, where('category', '==', selectedCategory));
      }

      if (isLoadMore && lastVisible) {
        q = query(q, startAfter(lastVisible));
      }

      const snapshot = await getDocs(q);
      const servicesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ServiceRequest[];

      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === PAGE_SIZE);

      if (isLoadMore) {
        setServices(prev => [...prev, ...servicesData]);
      } else {
        setServices(servicesData);
      }
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (serviceId: string) => {
    if (!auth.currentUser) {
      toast.error('Você precisa estar logado para se candidatar');
      return;
    }

    try {
      const applicantRef = doc(db, `services/${serviceId}/applicants/${auth.currentUser.uid}`);
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        toast.error('Perfil não encontrado');
        return;
      }

      const userData = userSnap.data();
      
      await setDoc(applicantRef, {
        userId: auth.currentUser.uid,
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        createdAt: serverTimestamp()
      });

      toast.success('Candidatura enviada com sucesso!');
    } catch (error) {
      console.error('Error applying to service:', error);
      toast.error('Erro ao enviar candidatura');
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    if (!auth.currentUser) return;

    try {
      await deleteDoc(doc(db, 'services', serviceId));
      setServices(prev => prev.filter(service => service.id !== serviceId));
      toast.success('Serviço excluído com sucesso');
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Erro ao excluir serviço');
    }
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold">Serviços Disponíveis</h1>
          <Button onClick={() => navigate('/servicos/nova-solicitacao')} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Nova Solicitação
          </Button>
        </div>

        <div className="mb-6">
          <ServiceFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            category={selectedCategory}
            onCategoryChange={setSelectedCategory}
            categories={CATEGORIES}
          />
        </div>

        <div className="space-y-6">
          {filteredServices.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              currentUserId={auth.currentUser?.uid || ''}
              onApply={handleApply}
              onDelete={handleDeleteService}
            />
          ))}

          {loading && (
            <div className="text-center py-8">
              <p>Carregando serviços...</p>
            </div>
          )}

          {!loading && filteredServices.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">Nenhum serviço encontrado</p>
            </div>
          )}

          {hasMore && !loading && (
            <div className="text-center">
              <Button variant="outline" onClick={() => loadServices(true)}>
                Carregar mais
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}